import { PokerRunEvent } from "@models/PokerRunEvent";
import * as Sentry from "@sentry/vue";
import { StateTree, defineStore } from "pinia";
import PokerRunEventDataService from "@/services/PokerRunEventDataService";
import { Stop } from "@models/Stop";
import { Item } from "@models/Item";
import Constants from "@/models/Constants";
import { ConnectedAccountResponse } from "@models/ConnectedAccountResponse";
import * as GeoFire from "geofire-common";
import ConnectedAccountDataService from "@/services/ConnectedAccountDataService";
import EventManagerDataService from "@/services/EventManagerDataService";
import useCodeGenerator from "@/modules/useCodeGenerator";
import { Unsubscribe } from "firebase/firestore";
import { EventManager } from "@models/EventManager";
import useDate from "@/modules/useDate";
import { useUserProfileStore } from "./UserProfile";
import { Tracker } from "@models/Tracker";

const { getDate, formatISODatetime, formatISODate, getNewDate, getDayOffset } = useDate();

const { generateCode, generateInternalId } = useCodeGenerator();

const getDefaultTime = () => {
  const date = new Date();
  date.setHours(8, 0, 0, 0);
  return formatISODate(date);
};

const defaultStop = {
  checkInCode: "",
  name: "",
  order: 1,
  cardsPerStop: 1,
  expectedArrivalTime: getDefaultTime(),
  expectedDepartureTime: getDefaultTime(),
  address: {
    addressLine1: "",
    city: "",
    state: "",
    postalCode: "",
    location: null,
  },
} as Stop;

const defaultItem = {
  itemId: "",
  name: "",
  amount: 0,
  required: false,
  order: 1,
  itemType: Constants.CUSTOM_TYPE,
  itemOptions: null,
  purchaseByDate: "",
  hasTracker: false,
} as Item;

const extraCardItem = {
  itemId: "",
  name: Constants.EXTRA_CARD,
  amount: null as unknown as number,
  itemType: Constants.EXTRA_CARD_TYPE,
  itemOptions: null,
} as Item;

export const useManageEventStore = defineStore("manageEvent", {
  state: () => {
    const registrationFee = {
      itemId: generateInternalId(),
      name: Constants.REGISTRATION,
      amount: null as unknown as number,
      itemType: Constants.FEE_TYPE,
      itemOptions: null,
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayString = formatISODate(today);
    const currentEvent = {
      id: generateInternalId(),
      name: null,
      status: "setup",
      description: null,
      eventType: null,
      eventDate: todayString,
      fee: null,
      stops: [],
      eventSettings: {
        registrationStartDate: todayString,
        gameFormat: "Participant Decks",
        isServiceFeeIncluded: false,
        registrationStatus: "open",
        cardReleaseType: "manual",
        cardReleaseStyle: "relaxed",
        isPrivateEvent: false,
        isMultidayEvent: false,
        excludeInSearchResults: false,
        enableExtraCard: false,
        allowInAppPurchase: true,
        allowExtraCardPurchaseAfterRegistration: true,
      },
      organizer: {},
      organizerId: "",
      paymentAccount: {},
      lineItems: [{ ...registrationFee }],
      trackers: [],
      waiver: false,
      setupEventInfoComplete: false,
      setupStopsComplete: false,
      setupWaiverComplete: true,
      setupPaymentAccountComplete: false,
      setupExtraCardComplete: true,
      setupLineItemsComplete: false,
      checkInAllCode: generateCode(6),
    } as unknown as PokerRunEvent;

    currentEvent.stops.push(createStop(currentEvent));

    const defaultState = {
      isNew: true,
      managers: [] as EventManager[],
      currentEvent: { ...currentEvent },
      unsubscribe: null as Unsubscribe | null,
      stripeClientSecret: "",
      stripeExpiresAt: 0,
      maxStepCompleted: 0,
    };
    return { ...defaultState };
  },
  actions: {
    async loadEvent(eventId: string): Promise<void> {
      this.$reset();
      this.isNew = false;
      const pokerRunEvent = await PokerRunEventDataService.get(eventId);
      this.handleEventChange(pokerRunEvent);
      if (this.unsubscribe) {
        this.unsubscribe();
      }
      this.unsubscribe = await PokerRunEventDataService.getSubscription(
        eventId,
        this.handleEventChange
      );

      ConnectedAccountDataService.warmup();
    },
    handleEventChange(pokerRunEvent: PokerRunEvent): void {
      Sentry.setContext("event", {
        eventId: pokerRunEvent,
        eventName: pokerRunEvent.name,
      });
      this.currentEvent = pokerRunEvent;
      const { profile } = useUserProfileStore() as unknown as { profile: { id: string } };
      if (this.currentEvent.organizerId === profile.id) {
        this.loadManagers();
      }
    },
    async save(): Promise<void> {
      await PokerRunEventDataService.save(this.currentEvent);
    },
    async setEvent(pokerRunEvent: PokerRunEvent) {
      this.isNew = false;
      syncStopDates(pokerRunEvent);
      if (pokerRunEvent.stops.length > 0) {
        setEventGeoHash(this.currentEvent);
      }
      this.currentEvent = await PokerRunEventDataService.save(pokerRunEvent);
    },
    setEventStops(stops: Stop[]) {  
      this.currentEvent.stops = stops;
      setEventGeoHash(this.currentEvent);
    },
    async createConnectedAccount(eventid: string | null): Promise<ConnectedAccountResponse> {
      const id = eventid || this.currentEvent.id;
      return ConnectedAccountDataService.createConnectedAccount(id);
    },
    async copyConnectedAccount(sourceId: string): Promise<void> {
      await ConnectedAccountDataService.copyConnectedAccount(sourceId, this.currentEvent.id);
      this.loadEvent(this.currentEvent.id);
    },
    createStop(): Stop {
      return createStop(this.currentEvent);
    },
    addStop(newStop: Stop) {
      const stop = { ...newStop };
      // eslint-disable-next-line operator-linebreak
      stop.order =
        this.currentEvent.stops
          .map((x: Stop) => x.order)
          .reduce((p: Stop, c: Stop) => (p > c ? p : c), 0) + 1;
      this.currentEvent.stops.push(stop);
      this.currentEvent.setupStopsComplete = true;
    },
    updateStop(stop: Stop, saveEvent = true) {
      const stops = [...this.currentEvent.stops];
      stops[stop.order - 1] = stop;
      // stops.sort(
      //   (a, b) =>
      //     new Date(a.expectedArrivalTime as string).getTime() -
      //     new Date(b.expectedArrivalTime as string).getTime()
      // );

      // eslint-disable-next-line no-plusplus
      // for (let index = 0; index < stops.length; index++) {
      //   stops[index].order = index + 1;
      // }

      this.currentEvent.stops = stops;
      this.currentEvent.setupStopsComplete = true;
      if (saveEvent) {
        this.setEvent(this.currentEvent);
      }
    },
    removeStop(stop: Stop) {
      const foundStop = this.currentEvent.stops.find((x: Stop) => x.order === stop.order) as Stop;
      this.currentEvent.stops.splice(this.currentEvent.stops.indexOf(foundStop), 1);
      let order = 1;
      this.currentEvent.stops.forEach((item: Stop) => {
        // eslint-disable-next-line no-plusplus, no-param-reassign
        item.order = order++;
      });

      this.currentEvent.setupStopsComplete = this.currentEvent.stops.length > 0;
      this.setEvent(this.currentEvent);
    },
    createExtraCardLineItem(): Item {
      return { ...extraCardItem, ...{ itemId: generateInternalId() } };
    },
    createLineItem(): Item {
      return {
        ...defaultItem,
        ...{ purchaseByDate: this.currentEvent.eventDate as string, itemId: generateInternalId() },
      };
    },
    addLineItem(item: Item) {
      this.currentEvent.trackers = this.currentEvent.trackers ?? [];
      if (item.hasTracker) {
        const addOnTracker = { name: item.name, isAddOnTracker: true, trackerId: item.itemId };
        this.currentEvent.trackers.push(addOnTracker);
      }
      this.currentEvent.lineItems.push(item);
    },
    removeLineItem(item: Item) {
      const foundFee = this.currentEvent.lineItems.find((x: Item) => x.itemId === item.itemId);
      if (foundFee) {
        const tracker = this.currentEvent.trackers.find(
          (x: Tracker) => x.trackerId === item.itemId
        );
        if (item.hasTracker && tracker) {
          this.currentEvent.trackers.splice(this.currentEvent.trackers.indexOf(tracker), 1);
        }
        this.currentEvent.lineItems.splice(this.currentEvent.lineItems.indexOf(foundFee), 1);
      }
    },
    updateLineItem(updateItem: Item) {
      const foundItem = this.currentEvent.lineItems.find(
        (x: Item) => x.itemId === updateItem.itemId
      );
      this.currentEvent.trackers = this.currentEvent.trackers ?? [];
      if (foundItem) {
        const existingTracker = this.currentEvent.trackers.find(
          (x: Tracker) => x.trackerId === foundItem.itemId
        );

        foundItem.amount = updateItem.amount;
        foundItem.name = updateItem.name;
        foundItem.purchaseByDate = updateItem.purchaseByDate;
        foundItem.hasTracker = updateItem.hasTracker;
        if (updateItem.itemOptions) {
          foundItem.itemOptions = updateItem.itemOptions;
        } else {
          foundItem.itemOptions = null;
        }

        if (existingTracker && existingTracker?.name !== updateItem.name) {
          existingTracker.name = updateItem.name;
        }

        if (updateItem.hasTracker && !existingTracker) {
          this.currentEvent.trackers.push({
            name: updateItem.name,
            isAddOnTracker: true,
            trackerId: updateItem.itemId,
          });
        } else if (!updateItem.hasTracker && existingTracker) {
          this.currentEvent.trackers.splice(this.currentEvent.trackers.indexOf(existingTracker), 1);
        }
      }
    },
    async loadManagers(): Promise<EventManager[]> {
      const managers = await EventManagerDataService.getManagers(this.currentEvent.id);
      this.managers.splice(0, this.managers.length, ...managers);
      return managers;
    },
    async saveManager(manager: EventManager): Promise<EventManager> {
      const newManager = manager.id ? manager : { ...manager, ...{ id: generateInternalId() } };

      if (manager.userId) {
        // Initialize arrays if they don't exist
        if (!this.currentEvent.admins) {
          this.currentEvent.admins = [];
        }
        if (!this.currentEvent.assistants) {
          this.currentEvent.assistants = [];
        }

        // Check if the user is already in the correct array
        const isInAdmins = this.currentEvent.admins.includes(manager.userId);
        const isInAssistants = this.currentEvent.assistants.includes(manager.userId);

        if (manager.managerType === "admin") {
          // Only add to admins if not already there
          if (!isInAdmins) {
            this.currentEvent.admins.push(manager.userId);
          }
          // Remove from assistants if present
          if (isInAssistants) {
            this.currentEvent.assistants = this.currentEvent.assistants.filter(
              (x: string) => x !== manager.userId
            );
          }
        } else if (manager.managerType === "assistant") {
          // Only add to assistants if not already there
          if (!isInAssistants) {
            this.currentEvent.assistants.push(manager.userId);
          }
          // Remove from admins if present
          if (isInAdmins) {
            this.currentEvent.admins = this.currentEvent.admins.filter(
              (x: string) => x !== manager.userId
            );
          }
        }
        this.currentEvent = await PokerRunEventDataService.save(this.currentEvent);
      }
      return EventManagerDataService.saveManager(this.currentEvent.id, newManager);
    },
    async deleteManager(id: string) {
      const manager = this.managers.find((x: EventManager) => x.id === id);
      if (!manager) {
        return;
      }

      if (manager.managerType === "admin") {
        const admins = this.managers
          .filter(
            (x: EventManager) => x.managerType === "admin" && x.status === "accepted" && x.id !== id
          )
          .map((x: EventManager) => x.userId);
        await PokerRunEventDataService.setAdmins(this.currentEvent.id, admins);
      } else {
        const assistants = this.managers
          .filter(
            (x: EventManager) =>
              x.managerType === "assistant" && x.status === "accepted" && x.id !== id
          )
          .map((x: EventManager) => x.userId);
        await PokerRunEventDataService.setAssistants(this.currentEvent.id, assistants);
      }
      await EventManagerDataService.deleteManager(this.currentEvent.id, id);
      await this.loadManagers();
    },
    async deleteTracker(tracker: Tracker) {
      const foundTracker = this.currentEvent.trackers.find(
        (x: Tracker) => x.trackerId === tracker.trackerId
      );
      if (foundTracker) {
        this.currentEvent.trackers.splice(this.currentEvent.trackers.indexOf(foundTracker), 1);
        await this.setEvent(this.currentEvent);
      }
    },
    updateMaxStepCompleted(step: number) {
      if (step > this.maxStepCompleted) {
        this.maxStepCompleted = step;
      }
    },
    unpublishEvent() {
      this.currentEvent.status = "setup";
      this.setEvent(this.currentEvent);
    },
    publishEvent() {
      this.currentEvent.status = "active";
      this.setEvent(this.currentEvent);
    },
    closeRegistration() {
      this.currentEvent.eventSettings.registrationStatus = "closed";
      return this.setEvent(this.currentEvent);
    },
    openRegistration() {
      this.currentEvent.eventSettings.registrationStatus = "open";
      return this.setEvent(this.currentEvent);
    },
  },
  getters: {
    needPaymentsSetup(state: { currentEvent: PokerRunEvent }): boolean {
      return (
        !state.currentEvent._internal?.connectedAccountDetails?.onboarded &&
        state.currentEvent.eventSettings.allowInAppPurchase &&
        !this.freeEvent
      );
    },
    freeEvent(state: { currentEvent: PokerRunEvent }): boolean {
      return (
        state.currentEvent.lineItems.every((item) => item.amount === 0) &&
        state.currentEvent._internal?.serviceFee === 0
      );
    },
    isUSEvent(state: { currentEvent: PokerRunEvent }): boolean {
      return state.currentEvent.stops.some((stop: Stop) => stop.address.country === "US");
    },
    isMultiDayEvent(state: { currentEvent: PokerRunEvent }): boolean {
      return state.currentEvent.stops.some((stop: Stop) => {
        const expectedArrivalTime = stop.expectedArrivalTime as string;
        const expectedDepartureTime = stop.expectedDepartureTime as string;
        const eventDate = state.currentEvent.eventDate as string;
        return (
          !expectedArrivalTime.startsWith(eventDate) || !expectedDepartureTime.startsWith(eventDate)
        );
      });
    },
    overlappingStops(state: { currentEvent: PokerRunEvent }): Stop[] {
      const stops = [...state.currentEvent.stops];
      const overlappingStops = [] as Stop[];
      stops.sort(
        (a, b) =>
          new Date(a.expectedArrivalTime as string).getTime() -
          new Date(b.expectedArrivalTime as string).getTime()
      );

      for (let index = 1; index < stops.length; index++) {
        const currentStop = stops[index];

        if (!currentStop.expectedArrivalTime && !currentStop.expectedCardDealTime) {
          continue;
        }

        const overlapping = stops.filter(
          (x) =>
            x.expectedDepartureTime > currentStop.expectedArrivalTime && x.order < currentStop.order
        );
        
        const overlappingCardDeal = stops.filter(
          (x) =>
            x.expectedCardDealTime &&
            currentStop.expectedCardDealTime &&
            x.expectedCardDealTime > currentStop.expectedCardDealTime &&
            x.order < currentStop.order
        );

        if (overlapping.length > 0 || overlappingCardDeal.length > 0) {
          overlappingStops.push(currentStop);

          overlapping.forEach((stop) => {
            if (!overlappingStops.includes(stop)) {
              overlappingStops.push(stop);
            }
          });
        }
      }
      return overlappingStops;
    },
    validateEvent(state: { currentEvent: PokerRunEvent }): boolean {
      return (
        state.currentEvent.setupEventInfoComplete &&
        state.currentEvent.setupStopsComplete &&
        (!state.currentEvent.eventSettings?.allowInAppPurchase ||
          this.isUSEvent ||
          state.currentEvent._internal?.connectedAccountDetails?.onboarded)
      );
    },
  },
  persist: true,
});

export const syncStopDates = (event: PokerRunEvent) => {
  const dayOffset = getDayOffset(
    event.eventDate as string,
    event.stops[0].expectedArrivalTime as string
  ) as number;
  event.stops.forEach((stop) => {
    stop.expectedArrivalTime = formatISODatetime(
      getNewDate(new Date(stop.expectedArrivalTime as string), dayOffset)
    );
    stop.expectedCardDealTime = stop.expectedCardDealTime ?? stop.expectedArrivalTime;
    stop.expectedCardDealTime = formatISODatetime(
      getNewDate(new Date(stop.expectedCardDealTime as string), dayOffset)
    );
    stop.expectedDepartureTime = formatISODatetime(
      getNewDate(new Date(stop.expectedDepartureTime as string), dayOffset)
    );
  });
};

export const createStop = (currentEvent: PokerRunEvent) => {
  const checkInCode = generateCode(6);
  const lastStop =
    currentEvent.stops && currentEvent.stops.length > 0
      ? currentEvent.stops[currentEvent.stops.length - 1]
      : null;

  const tempDate = getDate(currentEvent.eventDate as string) ?? new Date();
  tempDate.setHours(7, 0, 0, 0);
  const expectedArrival = lastStop?.expectedDepartureTime
    ? new Date(lastStop.expectedDepartureTime as string)
    : tempDate;
  expectedArrival.setHours(expectedArrival.getHours() + 1);
  const expectedArrivalTime = formatISODatetime(expectedArrival);
  const expectedCardDealTime = formatISODatetime(expectedArrival);
  expectedArrival.setHours(expectedArrival.getHours() + 1);
  const expectedDepartureTime = formatISODatetime(expectedArrival);
  const order = currentEvent.stops.length + 1;
  const newStop = {
    ...defaultStop,
    order,
    checkInCode,
    expectedArrivalTime,
    expectedCardDealTime,
    expectedDepartureTime,
  };
  newStop.address = {
    ...defaultStop.address,
  };
  return newStop;
};

const setEventGeoHash = (event: PokerRunEvent) => {
  if (
    event.stops[0].address.location &&
    (event.long !== event.stops[0].address.location?._long ||
      event.lat !== event.stops[0].address.location?._lat)
  ) {
    event.lat = event.stops[0].address.location._lat;
    event.long = event.stops[0].address.location._long;

    event.geoHash = GeoFire.geohashForLocation([event.lat, event.long]);
  }
};
